import type { RealEstateContact } from "../../../@types/sharedSchema";

/**
 * WHAT THE CONTACT PICKER LISTS — the pure rule behind its menu.
 *
 * Split out of `NewContactPicker.tsx` because the component imports the search and lookup
 * hooks, which import the app contexts, which a unit test cannot load. The rule that decides
 * WHEN "add this number" is offered is the one worth testing without a browser: offered too
 * early, it invites the realtor to add a person who is already a contact.
 */

/** What an option needs in order to be drawn. A summary and a whole contact both fit. */
export type ContactPickerOption = Pick<
  RealEstateContact,
  "id" | "callerFirstName" | "callerLastName" | "crmIsHot"
> & {
  /** The synthetic "add this number" row. */
  hint?: boolean;
};

const fullNameOf = (option: ContactPickerOption): string =>
  `${option.callerFirstName ?? ""} ${option.callerLastName ?? ""}`.trim();

/** The same two things the realtor types — a name, or a phone with or without its leading 0. */
export const matchesTyped = (option: ContactPickerOption, needle: string): boolean => {
  const digits = needle.startsWith("0") ? needle.slice(1) : needle;
  return fullNameOf(option).toLowerCase().includes(needle) || (option.id ?? "").includes(digits);
};

export type ContactPickerOptionsInput = {
  /** Options the host supplies. Listed first; filtered by what was typed. */
  pinnedOptions?: readonly ContactPickerOption[];
  /** What the server search returned for `term`, most recent first. */
  searchResults: readonly ContactPickerOption[];
  /** The search for `term` has not answered yet (`searchResults` may be the previous term). */
  searching: boolean;
  /** What the realtor typed, trimmed. Empty when nothing was. */
  term: string;
  /** `term` as an E.164 phone, when it is a complete number. */
  typedPhone: string | null;
  /**
   * The lookup of `typedPhone`: `undefined` = not answered, `null` = not a contact, or the
   * contact. Ignored when `typedPhone` is null.
   */
  typedContact: ContactPickerOption | null | undefined;
};

/**
 * The menu, in order:
 *
 *  1. the host options that match what was typed;
 *  2. the contact whose phone was typed in full, when there is one;
 *  3. the search results — hot leads first, as the menu has always listed them, otherwise
 *     in the order the server returned (most recently touched first).
 *
 * One row per id: the first place a contact appears is where it stays.
 *
 * And, only when NOTHING is listed, the "add this number" row — under three conditions that
 * all have to hold:
 *
 *  - something was typed;
 *  - the search for THAT text has come back (while it has not, an empty menu means "still
 *    asking", not "nobody matches");
 *  - if the text is a complete phone number, the lookup has ANSWERED that it is not a
 *    contact. `undefined` is not that answer.
 *
 * Text that is not a phone number still gets the row: the picker draws it as "not a valid
 * phone number", which is how the realtor learns why nothing can be added.
 */
export const buildContactPickerOptions = ({
  pinnedOptions,
  searchResults,
  searching,
  term,
  typedPhone,
  typedContact
}: ContactPickerOptionsInput): ContactPickerOption[] => {
  const needle = term.toLowerCase();
  const byId = new Map<string, ContactPickerOption>();
  const add = (option: ContactPickerOption): void => {
    if (option.id && !byId.has(option.id)) byId.set(option.id, option);
  };

  (pinnedOptions ?? []).filter((option) => !needle || matchesTyped(option, needle)).forEach(add);
  if (typedPhone && typedContact) add(typedContact);
  [...searchResults]
    .sort((a, b) => Number(b.crmIsHot === true) - Number(a.crmIsHot === true))
    .forEach(add);

  const options = [...byId.values()];

  const nothingMatches = options.length === 0 && Boolean(term) && !searching;
  const phoneIsFree = typedPhone ? typedContact === null : true;
  if (nothingMatches && phoneIsFree) options.push({ id: term, hint: true } as ContactPickerOption);

  return options;
};
